import { PART_FINDER_ACTIVITY_QUERY_KEY } from '@/hooks/api/part-finder-activity/query';
import { PART_FINDER_BATCH_QUERY_KEY, PART_FINDER_BATCHES_QUERY_KEY } from '@/hooks/api/part-finder-batches/query';
import { PART_FINDER_REPORTS_QUERY_KEY } from '@/hooks/api/part-finder-reports/query';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { pluralize } from '@/utils/format';
import { useQueryClient } from '@tanstack/react-query';
import { BulkDeleteModal } from '../modals/BulkDeleteModal';

interface ActivityDeleteItem {
  id: string;
  kind: 'batch' | 'report';
  targetId: string;
}

export const BulkDeleteActivityModal = ({
  open,
  onClose,
  selectedBatchIds,
  selectedReportIds,
  onDeleteComplete,
}: {
  open: boolean;
  onClose: () => void;
  selectedBatchIds: string[];
  selectedReportIds: string[];
  onDeleteComplete: () => void;
}) => {
  const { getTenantId } = useTenants();
  const queryClient = useQueryClient();

  const items: ActivityDeleteItem[] = [
    ...selectedBatchIds.map(id => ({ id: `batch:${id}`, kind: 'batch' as const, targetId: id })),
    ...selectedReportIds.map(id => ({ id: `report:${id}`, kind: 'report' as const, targetId: id })),
  ];

  const handleDeleteItem = async (item: ActivityDeleteItem) => {
    const tenantId = getTenantId();
    if (item.kind === 'batch') {
      await api.tenants.deletePartFinderBatch(tenantId, item.targetId);
      return;
    }
    try {
      await api.tenants.deletePartFinderReport(tenantId, item.targetId);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return;
      throw error;
    }
  };

  const handleDeleteComplete = () => {
    queryClient.resetQueries({ queryKey: [PART_FINDER_ACTIVITY_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [PART_FINDER_REPORTS_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [PART_FINDER_BATCHES_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [PART_FINDER_BATCH_QUERY_KEY], exact: false });
    onDeleteComplete();
  };

  return (
    <BulkDeleteModal
      open={open}
      onClose={onClose}
      selectedItems={items}
      onDeleteComplete={handleDeleteComplete}
      deleteItem={handleDeleteItem}
      itemLabel={{ singular: 'item', plural: 'items' }}
      description={count =>
        `${pluralize(count, 'item', 'items')} will be permanently deleted. Deleting a batch also deletes all of its reports. This action cannot be undone.`
      }
    />
  );
};
